// Inline-expand variant of the Insurance form — wraps InsuranceFormFields
// in the brand-wash container used inside an expanded TrackingTable row.
// Unlike the modal, inline-expand auto-focuses the first input on mount
// so keyboard users land on an editable field, and returns focus to the
// row's toggle button on unmount via the `returnFocusRef` callback.

import { useEffect, useRef, useState } from "react";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import InsuranceFormFields from "./InsuranceFormFields";
import {
  stateFromInsurance,
  toCreateInsuranceInput,
  type InsuranceFieldErrors,
  type InsuranceFormState,
} from "./insuranceFormState";
import { isValidIsoDate } from "../shared/isoDateValidation";
import type {
  Insurance,
  UpdateInsuranceInput,
} from "../../../../types";

function validate(state: InsuranceFormState): InsuranceFieldErrors {
  const errors: InsuranceFieldErrors = {};
  if (state.category === "insurance") {
    if (state.insuranceTypes.length === 0) {
      errors.insuranceTypes = "Select at least one insurance type";
    }
    if (
      state.insuranceTypes.includes("other") &&
      !state.otherInsuranceName.trim()
    ) {
      errors.otherInsuranceName = "Enter a name for the other insurance type";
    }
    if (
      state.insuranceTypes.includes("workers-compensation") &&
      !state.workersCompensationState.trim()
    ) {
      errors.workersCompensationState =
        "Enter the state for workers compensation";
    }
  } else {
    if (!state.subjectName.trim()) {
      errors.subjectName =
        state.category === "licence"
          ? "Licence name is required"
          : "Accreditation name is required";
    }
  }
  if (!state.provider.trim()) errors.provider = "Provider is required";
  if (!isValidIsoDate(state.startDate)) {
    errors.startDate = "Enter a valid start date";
  }
  if (!isValidIsoDate(state.expiryDate)) {
    errors.expiryDate = "Enter a valid expiry date";
  }
  if (
    !errors.startDate &&
    !errors.expiryDate &&
    state.expiryDate < state.startDate
  ) {
    errors.expiryDate = "Expiry date must be on or after start date";
  }
  return errors;
}

interface InsuranceInlineExpandProps {
  item: Insurance;
  contractId: string;
  onUpdate: (input: UpdateInsuranceInput) => Promise<unknown>;
  onCancel: () => void;
  /**
   * Called after unmount so the parent can restore focus to the row's
   * expand toggle. The parent should keep a ref to the toggle and call
   * `.focus()` on it.
   */
  onClosed?: () => void;
  isSubmitting?: boolean;
  providerSuggestions?: string[];
}

export default function InsuranceInlineExpand({
  item,
  contractId,
  onUpdate,
  onCancel,
  onClosed,
  isSubmitting,
  providerSuggestions,
}: InsuranceInlineExpandProps) {
  const [state, setState] = useState<InsuranceFormState>(() =>
    stateFromInsurance(item),
  );
  const [errors, setErrors] = useState<InsuranceFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Auto-focus first interactive input on mount
  useEffect(() => {
    const first = rootRef.current?.querySelector<HTMLElement>(
      'input:not([type="checkbox"]), select, textarea',
    );
    first?.focus();
  }, []);

  // Restore focus to the trigger (row toggle) on unmount
  useEffect(() => {
    return () => {
      onClosed?.();
    };
  }, [onClosed]);

  const handleChange = (patch: Partial<InsuranceFormState>) => {
    setState((prev) => ({ ...prev, ...patch }));
    if (Object.keys(patch).some((k) => k in errors)) {
      setErrors((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(patch)) {
          delete next[k as keyof InsuranceFieldErrors];
        }
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate(state);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    setSubmitError(null);
    try {
      const input: UpdateInsuranceInput = {
        version: item.version,
        ...toCreateInsuranceInput(state, contractId),
      };
      await onUpdate(input);
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to save insurance.",
      );
    }
  };

  return (
    <div ref={rootRef} data-testid="insurance-inline-expand">
      <form onSubmit={handleSubmit} className="space-y-4">
        <InsuranceFormFields
          value={state}
          onChange={handleChange}
          errors={errors}
          idPrefix={`ins-inline-${item.id}`}
          disabled={isSubmitting}
          providerSuggestions={providerSuggestions}
        />
        {submitError && <Alert variant="error">{submitError}</Alert>}
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Save changes
          </Button>
        </div>
      </form>
    </div>
  );
}
